import React, { useState, useRef, useEffect } from 'react';
import { Sparkles, ArrowRight, ArrowLeft, RotateCcw, Star, Heart, Shield, Zap, Moon, Sun, Eye, Gem } from 'lucide-react';

const questions = [
  {
    id: 'zodiac',
    title: 'What is your zodiac sign?',
    subtitle: 'The stars hold your crystal key',
    options: [
      { label: 'Aries', value: 'Aries', symbol: '\u2648', color: '#FF6B6B' },
      { label: 'Taurus', value: 'Taurus', symbol: '\u2649', color: '#00C853' },
      { label: 'Gemini', value: 'Gemini', symbol: '\u264A', color: '#FFB800' },
      { label: 'Cancer', value: 'Cancer', symbol: '\u264B', color: '#A8B8C8' },
      { label: 'Leo', value: 'Leo', symbol: '\u264C', color: '#FF9F43' },
      { label: 'Virgo', value: 'Virgo', symbol: '\u264D', color: '#3AAFA9' },
      { label: 'Libra', value: 'Libra', symbol: '\u264E', color: '#3B82F6' },
      { label: 'Scorpio', value: 'Scorpio', symbol: '\u264F', color: '#2C2C2C' },
      { label: 'Sagittarius', value: 'Sagittarius', symbol: '\u2650', color: '#30B0A0' },
      { label: 'Capricorn', value: 'Capricorn', symbol: '\u2651', color: '#7B1818' },
      { label: 'Aquarius', value: 'Aquarius', symbol: '\u2652', color: '#9D4EDD' },
      { label: 'Pisces', value: 'Pisces', symbol: '\u2653', color: '#5BB8D4' },
    ],
  },
  {
    id: 'intention',
    title: 'What is your deepest intention?',
    subtitle: 'Your soul speaks — the crystal listens',
    options: [
      { label: 'Attract Love', value: 'love', icon: Heart, color: '#FF6B9D' },
      { label: 'Find Protection', value: 'protection', icon: Shield, color: '#2D3436' },
      { label: 'Manifest Abundance', value: 'abundance', icon: Sparkles, color: '#FFB800' },
      { label: 'Gain Clarity', value: 'clarity', icon: Eye, color: '#9D4EDD' },
      { label: 'Heal & Release', value: 'healing', icon: Heart, color: '#00C853' },
      { label: 'Build Confidence', value: 'confidence', icon: Zap, color: '#FF9F43' },
    ],
  },
  {
    id: 'element',
    title: 'Which element calls to you?',
    subtitle: 'Close your eyes and feel the pull',
    options: [
      { label: 'Fire', value: 'Fire', desc: 'Passion, energy, transformation', color: '#FF6B6B', icon: Zap },
      { label: 'Earth', value: 'Earth', desc: 'Stability, growth, grounding', color: '#00C853', icon: Shield },
      { label: 'Air', value: 'Air', desc: 'Wisdom, communication, freedom', color: '#9D4EDD', icon: Star },
      { label: 'Water', value: 'Water', desc: 'Intuition, emotion, flow', color: '#5BB8D4', icon: Moon },
    ],
  },
  {
    id: 'challenge',
    title: 'What weighs heaviest on your spirit?',
    subtitle: 'Name it so you can release it',
    options: [
      { label: 'Anxiety & Stress', value: 'stress', color: '#9D4EDD' },
      { label: 'Heartache & Loss', value: 'heartache', color: '#FF6B9D' },
      { label: 'Low Energy & Fatigue', value: 'fatigue', color: '#FF9F43' },
      { label: 'Confusion & Indecision', value: 'confusion', color: '#3B82F6' },
      { label: 'Negativity & Toxic Energy', value: 'negativity', color: '#2D3436' },
      { label: 'Feeling Stuck', value: 'stuck', color: '#7B1818' },
    ],
  },
];

// Crystal matching database
const crystalProfiles = [
  { name: 'Amethyst', zodiac: 'Aquarius', element: 'Air', intentions: ['clarity', 'healing'], challenges: ['stress', 'confusion'], color: '#9D4EDD', chakra: 'Crown', message: 'Amethyst is your gateway to higher consciousness. Its serene violet frequency dissolves anxiety, sharpens intuition, and connects you directly to your inner wisdom. Place it on your nightstand to invite prophetic dreams.' },
  { name: 'Rose Quartz', zodiac: 'Taurus', element: 'Earth', intentions: ['love', 'healing'], challenges: ['heartache', 'stress'], color: '#FF6B9D', chakra: 'Heart', message: 'Rose Quartz wraps your heart in pure, unconditional love. It gently dissolves the walls you\'ve built around your most tender places, teaching you that vulnerability is not weakness — it is your greatest strength.' },
  { name: 'Carnelian', zodiac: 'Aries', element: 'Fire', intentions: ['confidence', 'abundance'], challenges: ['fatigue', 'stuck'], color: '#E05533', chakra: 'Sacral', message: 'Carnelian ignites the fire within you. This stone of bold action and creative power has been carried by warriors and leaders for millennia. It transforms lethargy into unstoppable momentum.' },
  { name: 'Citrine', zodiac: 'Gemini', element: 'Air', intentions: ['abundance', 'confidence'], challenges: ['stuck', 'fatigue'], color: '#E6A817', chakra: 'Solar Plexus', message: 'Citrine is liquid sunshine crystallized. Known as the Merchant\'s Stone, it magnetically attracts prosperity while radiating warmth, optimism, and personal power into every corner of your life.' },
  { name: 'Moonstone', zodiac: 'Cancer', element: 'Water', intentions: ['clarity', 'healing'], challenges: ['confusion', 'stress'], color: '#A8B8C8', chakra: 'Crown', message: 'Moonstone carries the luminous energy of every lunar cycle within its iridescent glow. It heightens your natural intuition, guides you through transitions, and reminds you that endings are simply new beginnings in disguise.' },
  { name: 'Tiger Eye', zodiac: 'Leo', element: 'Fire', intentions: ['confidence', 'protection'], challenges: ['fatigue', 'stuck'], color: '#B8860B', chakra: 'Solar Plexus', message: 'Tiger Eye is the stone of the fearless. Its golden-brown bands shift like a tiger\'s gaze, combining the power of the earth with the energy of the sun. It turns self-doubt into unwavering confidence.' },
  { name: 'Amazonite', zodiac: 'Virgo', element: 'Earth', intentions: ['healing', 'clarity'], challenges: ['stress', 'heartache'], color: '#3AAFA9', chakra: 'Heart & Throat', message: 'Amazonite is the peacemaker stone. Its soothing teal energy calms the spirit while empowering you to speak your truth with grace. It filters toxic energy and EMF, creating a sanctuary of harmony wherever you go.' },
  { name: 'Lapis Lazuli', zodiac: 'Libra', element: 'Air', intentions: ['clarity', 'confidence'], challenges: ['confusion', 'stuck'], color: '#1A3C7A', chakra: 'Third Eye', message: 'Lapis Lazuli, the stone of pharaohs and philosophers. Its deep celestial blue unlocks the doors of perception, granting access to wisdom that transcends time. It\'s the ultimate stone for seekers of truth.' },
  { name: 'Black Obsidian', zodiac: 'Scorpio', element: 'Water', intentions: ['protection', 'healing'], challenges: ['negativity', 'heartache'], color: '#2C2C2C', chakra: 'Root', message: 'Black Obsidian is forged from volcanic fire — raw, powerful, and unflinching. It acts as a spiritual mirror, revealing hidden truths while shielding you from psychic attack. This stone does not gentle — it transforms.' },
  { name: 'Turquoise', zodiac: 'Sagittarius', element: 'Fire', intentions: ['healing', 'protection'], challenges: ['negativity', 'stress'], color: '#30B0A0', chakra: 'Throat', message: 'Turquoise is one of the oldest known healing stones on Earth. Sacred to Native American, Egyptian, and Tibetan cultures alike, it bridges heaven and earth, offering protection, healing, and the courage to speak your truth.' },
  { name: 'Garnet', zodiac: 'Capricorn', element: 'Earth', intentions: ['confidence', 'abundance'], challenges: ['fatigue', 'stuck'], color: '#7B1818', chakra: 'Root', message: 'Garnet pulses with the deep, primal energy of the Earth itself. This stone of passionate devotion revitalizes your life force, anchors your ambitions into reality, and reminds you that slow, steady fire burns longest.' },
  { name: 'Aquamarine', zodiac: 'Pisces', element: 'Water', intentions: ['clarity', 'healing'], challenges: ['stress', 'confusion'], color: '#5BB8D4', chakra: 'Throat', message: 'Aquamarine is like breathing the ocean itself. Its cool, clarifying energy washes away fear, calms the mind, and opens the channels of clear, compassionate communication. Sailors once carried it for safe passage — let it guide yours.' },
  { name: 'Selenite', zodiac: null, element: 'Air', intentions: ['clarity', 'healing'], challenges: ['negativity', 'stress'], color: '#D8D0C8', chakra: 'Crown', message: 'Selenite is pure crystallized light. Named after the moon goddess Selene, it radiates such high vibration that negativity simply cannot exist in its presence. It cleanses, charges, and elevates everything it touches — including you.' },
  { name: 'Black Tourmaline', zodiac: null, element: 'Earth', intentions: ['protection', 'confidence'], challenges: ['negativity', 'fatigue'], color: '#2D3436', chakra: 'Root', message: 'Black Tourmaline creates an impenetrable energetic force field around your aura. It absorbs and transmutes negative energy, grounds scattered thoughts, and builds the kind of inner strength that nothing can shake.' },
  { name: 'Clear Quartz', zodiac: null, element: 'All', intentions: ['clarity', 'abundance'], challenges: ['confusion', 'stuck'], color: '#D0C8E0', chakra: 'Crown', message: 'Clear Quartz is the Master Healer — the most versatile and powerful stone on Earth. It amplifies any intention, harmonizes all chakras, and can be programmed for any purpose. It is pure, infinite possibility in crystalline form.' },
  { name: 'Smokey Quartz', zodiac: null, element: 'Earth', intentions: ['protection', 'healing'], challenges: ['stress', 'negativity'], color: '#6B5B4B', chakra: 'Root', message: 'Smokey Quartz is the gentle alchemist. It takes the heavy, dark energy you carry — the worry, the fear, the grief — and transmutes it into grounded, stable peace. It shows you that even shadows contain wisdom.' },
];

function scoreMatch(answers) {
  return crystalProfiles.map((crystal) => {
    let score = 0;
    if (answers.zodiac && crystal.zodiac === answers.zodiac) score += 5;
    if (answers.element && (crystal.element === answers.element || crystal.element === 'All')) score += 3;
    if (answers.intention && crystal.intentions.includes(answers.intention)) score += 4;
    if (answers.challenge && crystal.challenges.includes(answers.challenge)) score += 3;
    return { ...crystal, score };
  }).sort((a, b) => b.score - a.score);
}

const CrystalQuiz = () => {
  const [step, setStep] = useState(-1); // -1 = intro
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [animating, setAnimating] = useState(false);
  const sectionRef = useRef(null);

  const scrollToQuiz = () => {
    sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleStart = () => {
    setStep(0);
    setAnswers({});
    setResult(null);
    setTimeout(scrollToQuiz, 100);
  };

  const handleSelect = (questionId, value) => {
    const newAnswers = { ...answers, [questionId]: value };
    setAnswers(newAnswers);
    setAnimating(true);

    setTimeout(() => {
      if (step < questions.length - 1) {
        setStep(step + 1);
      } else {
        const ranked = scoreMatch(newAnswers);
        setResult(ranked[0]);
      }
      setAnimating(false);
      setTimeout(scrollToQuiz, 100);
    }, 400);
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
      setTimeout(scrollToQuiz, 100);
    }
  };

  const handleReset = () => {
    setStep(-1);
    setAnswers({});
    setResult(null);
  };

  const currentQ = step >= 0 && step < questions.length ? questions[step] : null;

  return (
    <div ref={sectionRef} data-testid="crystal-quiz">
      {/* Intro */}
      {step === -1 && !result && (
        <div className="text-center py-4">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#9D4EDD] to-[#c9a96e] flex items-center justify-center mx-auto mb-6 shadow-xl shadow-[#9D4EDD]/20">
            <Gem className="w-9 h-9 text-white" />
          </div>
          <h3 className="font-serif text-2xl md:text-3xl text-[#1e1919] mb-3">
            Find Your Crystal
          </h3>
          <p className="text-sm text-[#61525a] max-w-md mx-auto mb-8">
            Answer 4 quick questions and we'll reveal the crystal the universe has chosen for you.
          </p>
          <button
            onClick={handleStart}
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-[#9D4EDD] to-[#FF6B9D] text-white rounded-xl font-semibold hover:shadow-xl hover:shadow-[#9D4EDD]/30 hover:scale-105 transition-all"
            data-testid="quiz-start-btn"
          >
            <Sparkles className="w-4 h-4" />
            Begin the Reading
          </button>
        </div>
      )}

      {/* Question */}
      {currentQ && !result && (
        <div className={`transition-all duration-300 ${animating ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
          {/* Progress */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex gap-1.5">
              {questions.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all duration-500 ${
                    i <= step ? 'w-8 bg-gradient-to-r from-[#9D4EDD] to-[#c9a96e]' : 'w-4 bg-[#e8e4de]'
                  }`}
                />
              ))}
            </div>
            <span className="text-xs text-[#7A7A9A]">{step + 1} of {questions.length}</span>
          </div>

          <div className="text-center mb-8">
            <h3 className="font-serif text-xl md:text-2xl text-[#1e1919] mb-1">{currentQ.title}</h3>
            <p className="text-sm text-[#7A7A9A] italic">{currentQ.subtitle}</p>
          </div>

          {/* Options */}
          <div className={`grid gap-3 ${
            currentQ.id === 'zodiac' ? 'grid-cols-3 sm:grid-cols-4' :
            currentQ.id === 'element' ? 'grid-cols-2' :
            'grid-cols-2 sm:grid-cols-3'
          }`}>
            {currentQ.options.map((opt) => {
              const isSelected = answers[currentQ.id] === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => handleSelect(currentQ.id, opt.value)}
                  className={`relative p-4 rounded-xl border-2 text-center transition-all duration-300 hover:scale-[1.03] ${
                    isSelected
                      ? 'border-[#9D4EDD] bg-[#9D4EDD]/5 shadow-lg'
                      : 'border-[#e8e4de] bg-white hover:border-[#9D4EDD]/40 hover:shadow-md'
                  }`}
                  data-testid={`quiz-option-${opt.value}`}
                >
                  {opt.symbol && (
                    <span className="text-2xl block mb-1" style={{ color: opt.color }}>{opt.symbol}</span>
                  )}
                  {opt.icon && !opt.symbol && (
                    <opt.icon className="w-6 h-6 mx-auto mb-1" style={{ color: opt.color }} />
                  )}
                  {!opt.icon && !opt.symbol && (
                    <div className="w-4 h-4 rounded-full mx-auto mb-2" style={{ backgroundColor: opt.color }} />
                  )}
                  <span className="text-sm font-semibold text-[#1e1919] block">{opt.label}</span>
                  {opt.desc && <span className="text-[10px] text-[#7A7A9A] mt-0.5 block">{opt.desc}</span>}
                </button>
              );
            })}
          </div>

          {step > 0 && (
            <button onClick={handleBack} className="mt-6 inline-flex items-center gap-1 text-sm text-[#7A7A9A] hover:text-[#9D4EDD] transition-colors" data-testid="quiz-back-btn">
              <ArrowLeft className="w-3.5 h-3.5" /> Previous
            </button>
          )}
        </div>
      )}

      {/* Result */}
      {result && (
        <div className="text-center animate-in fade-in" data-testid="quiz-result">
          {/* Crystal orb reveal */}
          <div className="relative inline-block mb-6">
            <div
              className="w-28 h-28 rounded-full mx-auto flex items-center justify-center shadow-2xl"
              style={{
                background: `linear-gradient(135deg, ${result.color}40, ${result.color}90)`,
                boxShadow: `0 0 40px ${result.color}30, 0 0 80px ${result.color}15`,
              }}
            >
              <Gem className="w-12 h-12 text-white" />
            </div>
            <div
              className="absolute -inset-4 rounded-full animate-pulse"
              style={{ background: `radial-gradient(circle, ${result.color}20, transparent 70%)` }}
            />
          </div>

          <p className="text-xs uppercase tracking-[0.3em] text-[#c9a96e] font-bold mb-2">Your Crystal Is</p>
          <h3 className="font-serif text-3xl md:text-4xl text-[#1e1919] mb-2">{result.name}</h3>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#9D4EDD]/10 text-[#9D4EDD] text-xs font-semibold mb-6">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: result.color }} />
            {result.chakra} Chakra
          </div>

          <p className="text-[#61525a] leading-relaxed max-w-lg mx-auto mb-8 text-sm">
            {result.message}
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={handleReset}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-[#9D4EDD] text-[#9D4EDD] rounded-xl font-semibold hover:bg-[#9D4EDD] hover:text-white transition-all text-sm"
              data-testid="quiz-retake-btn"
            >
              <RotateCcw className="w-4 h-4" />
              Take Again
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CrystalQuiz;
